import dobbyscan from 'dobbyscan';
import { listToMap, populateFromMap } from '../../utils/map';
import { sortByValue } from '../../utils/localeCompare';
import i18n from '../../i18n';

import TagService from '../../services/tag';
import FiltersService from '../../services/filters';

const namespaced = true;
//TODO remove categories/groups and use from their store
const state = {
  tags: [],
  mapTags: [],
  mapTagsScroll: null,
  totalRows: 0,
  filteredTotalRows: 0,
  categories: [],
  groups: [],
  history: [],
  filters: [],
  resetTags: false
}

const mutations = {
  setCategories(state, categories) {
    state.categories = categories;
  },
  setGroups(state, groups) {
    state.groups = groups;
  },
  setTags(state, tags) {
    state.tags = tags;
    state.totalRows = tags.total;
  },
  addTags(state, tags) {
    state.mapTags = state.mapTags.concat(tags);
    state.totalRows = tags.total;
  },
  clearMapTags(state){
    state.mapTags = [];
  },
  setMapTagsScroll(state, scroll){
    state.mapTagsScroll = scroll;
  },
  setTotal(state, total) {
    state.totalRows = total;
  },
  setFilteredTotal(state, total) {
    state.filteredTotalRows = total;
  },
  setHistory(state, history) {
    state.history = history;
  },
  clearTags(state) {
    state.tags = [];
  },
  setFilters(state, filters){
    state.filters.push(...filters);
  },
  clearFilters(state){
    state.filters = [];
  },
  setAreaFilters(state, areas){
    areas = areas.sort(sortByValue);
    let areaFilter = [{ 
      name: i18n.t('area'), 
      field: "areaName",
      filterPrefix: 'eq',
      cols: '6',
      options: areas
    }];
    state.filters.push(...areaFilter);
  },
  setLocationFilters(state, locations){
    locations = locations.sort(sortByValue);
    let filterLocations = [{ 
      name: i18n.t('location'), 
      field: "locationName",
      filterPrefix: 'eq',
      cols: '6',
      options: locations
    }];
    state.filters.push(...filterLocations);
  },
  setZoneFilters(state, zones){
    zones = zones.sort(sortByValue);
    let zoneFilter = [{ 
      name: i18n.t('zone'), 
        field: "zoneName",
        filterPrefix: 'eq',
        cols: '6',
        options: zones 
    }];
    state.filters.push(...zoneFilter);
  },
  setResetTags(state, reset){
    state.resetTags = reset;
  }
}

const getters = {
  tags: (state) => state.tags,
  populatedTags: (state, getters, rootState, rootGetters) => {
    const locations = rootGetters['location/locations'];
    const locationsMap = listToMap(locations, 'macAddress');
    const mappedTags = populateFromMap(state.mapTags, 'location', locationsMap, 'macAddress');
    return mappedTags;
  },
  totalRows: (state) => state.totalRows,
  filteredTotalRows: (state) => state.filteredTotalRows,
  filters: (state) => state.filters,
  categories: (state) => state.categories,
  groups: (state) => state.groups,
  getResetTags: (state) => state.resetTags,
  getMapTags: (state) => state.mapTags,
  getMapTagsScroll: (state) => state.mapTagsScroll
}

const actions = {
  clearFilters({commit}) {
    commit('clearFilters');
  },
  async getTags({ commit, getters }, params) {
    try {
      const response = await TagService.getTagsBySite(params);
      
      if(getters.getResetTags){//some filter was applied
        commit('clearTags');
        commit('clearMapTags');
        commit('addTags', response.tags);//save first page for list
        if(params.indoor){
          commit('setTags', response.tags.slice(0, params.pagination.size));//save first page for grid
        }else{
          commit('setTags', response.tags);//save first page for grid
        }
        commit('setResetTags', false);
        commit('setMapTagsScroll', null);
      }else{
        if(params.fromMap){
          commit('addTags', response.tags);
        }else{
          commit('setTags', response.tags);
          if(params.newSorting){
            commit('clearMapTags');
            if(params.pagination.page === 1){
              commit('addTags', response.tags);//save first page for list
            }else{
              const response = await TagService.getTagsBySite(params, true);
              commit('addTags', response.tags);//save first page for list
            }
          }
        }
      }
      commit('setTotal', response.total);
      commit('setFilteredTotal', response.filteredTotal);

      return response;
    } catch (e) {
      commit('setTags', []);
      commit('setTotal', 0);
      throw new Error(i18n.t('something-went-wrong'), e);
    }
  },
  async getTag({}, tagId){
    try {
      const response = await TagService.getTagById(tagId);
      return response;
    } catch (e) {
      throw new Error(i18n.t('something-went-wrong'), e);
    }
  },
  async createTag({}, {device}) {
    try {
      const resp = await TagService.createTag(device);
      return resp;
    } catch (error) {
      throw new Error(i18n.t('something-went-wrong'), error);
    }
  },
  async editTag({}, {device}) {
    try {
      const resp = await TagService.editTag(device);
      return resp;
    } catch (error) {
      throw new Error(i18n.t('something-went-wrong'), error);
    }
  },
  async deleteTag({}, {device}) {
    try {
      const resp = await TagService.deleteTag(device);
      return resp;
    } catch (error) {
      throw new Error(i18n.t('something-went-wrong'), error);
    }
  },
  async getTagsFilters({ commit, dispatch, rootGetters }, { siteId }){
    // all that in future will be just one API call, which will be returning the filter JSON
    // TODO replace all that for just one API call
    let filterCategories = await FiltersService.getFilterCategories(siteId);
    filterCategories = filterCategories.sort(sortByValue);
    let filterGroups = await FiltersService.getFilterGroups(siteId);
    filterGroups = filterGroups.sort(sortByValue);

    commit('setGroups', filterGroups);
    commit('setCategories', filterCategories);

    const currentSiteLabels = rootGetters['site/currentSite']?.assetInfo?.metadata?.props?.labels;

    const filters = [
      { 
        name: currentSiteLabels && currentSiteLabels.category ? currentSiteLabels.category : i18n.t('Category'), 
        field: "categoryName",
        filterPrefix: 'eq',
        cols: '6',
        options: filterCategories
      },
      { 
        name: currentSiteLabels && currentSiteLabels.group ? currentSiteLabels.group : i18n.t('Group'), 
        field: "groupName",
        filterPrefix: 'eq',
        cols: '6',
        options: filterGroups
      }
    ];
    //////////////
    commit('setFilters', filters);

    return filters;
  },
  
  async getTagHistory({ commit }, params) {
    try {
      const response = await TagService.getHistory(params)

      let geodata = {
        type: 'FeatureCollection',
        features: []
      };

      response.forEach(event => {
        if (event.longitude && event.time) {
          geodata.features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [event.longitude, event.latitude]
            },
            properties: {
              id: event.id,
              time: event.time.toISOString(),
              etype: event.etype.toLowerCase(),
              description: `<p style="color:black;">Date ${event.time.toLocaleDateString()}</p>
              <p style="color:black;">Time ${event.time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>`
            }
          });
        }
      });

      let clusterArray = dobbyscan(
        geodata.features,
        0.01,
        p => p.geometry.coordinates[0],
        p => p.geometry.coordinates[1]
      );

      let timed = [];
      clusterArray.forEach((cluster, clusterId) => {
        cluster.forEach(f => {
          timed.push({
            c: clusterId, 
            d: new Date(f.properties.time).getTime(),
            id: f.properties.id,
            source: f.properties.etype 
          });
        });
      });

      // TO DO Move to locale compare
      let sortedtimed = timed.sort((a, b) => {
        if (a.d > b.d) {
          return 1;
        }
        if (b.d > a.d) {
          return -1;
        }
        return 0;
      });

      let sortedevents = sortedtimed.splice(0, 1);
      let sortedl = sortedtimed.length; // Remaining Event Length
      
      // For each cluster, push first item
      // For other items in same cluster, set latest time
      let i = 0;
      while (i < sortedl) {
        let l = sortedevents.length;
        let nxt = sortedtimed.splice(0, 1)[0];
        let cur = sortedevents[l - 1];
        if (cur.c === nxt.c) {
          // Update continued time only if the timestamp is different
          if (cur.d !== nxt.d) {
            cur.cont = nxt.d;
          }
        } else {
          sortedevents.push(nxt);
        }
        i += 1;
      }

      let flatSortedEvents = [];
      sortedevents.forEach(event => {
        flatSortedEvents.push(event);
        if (event.cont) {
          flatSortedEvents.push({
            ...event,
            d: event.cont
          })
        }
      });

      let geoclustered = {
        type: 'FeatureCollection',
        features: []
      };

      // Map coordinates from Clustered Array to Sorted Events
      clusterArray.forEach((cl, idx) => {
        geoclustered.features.push(cl[0]);
        flatSortedEvents.forEach(s => {
          if (s.c === idx) {
            s.coordinates = cl[0].geometry.coordinates;
            s.id = cl[0].properties.id;
          }
        });
      });

      return {
        historyGeoJson: geoclustered,
        historyEventsList: flatSortedEvents 
      };
    } catch (e) {
      throw new Error(i18n.t('something-went-wrong'), e);
    }
  },
  async exportAllTags({}, params){
    try {
      const response = await TagService.exportAllTags(params);
      return response;
    }catch(e){
      throw new Error(i18n.t('something-went-wrong'), e);
    }
  }
}

export default {
  namespaced,
  state,
  mutations,
  getters,
  actions
}